import { Component, Input } from '@angular/core';
import { BandBadge, CvBandBadge, CvTextSegment, PercentLabel, ReadinessLine } from 'agu_app_shell';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { ResearchCareerMatches, ResearchJobProfile, ResearchRoleMatch } from '../../models/research.models';
import { PostingsOf, SkillCoverage } from './career-explorer.state';

/**
 * The footnote under the cards: the FIRST sentence of P6:460 only. Deviation (P6:460): the second sentence describes the striped
 * "what you would reach if every pending tag turned out to be true" part of each bar; the contract returns one 0..1 overlap, so
 * there is no range and no striped part.
 */
export const PATHS_FOOTNOTE = 'Each score is weighted coverage of what the occupation asks for, so holding skills it does not ask for never lowers it.';

/** Held-skill pills a card shows before "+n" (P6:1651 `have.slice(0,3)`). */
const MAX_HELD_PILLS = 3;

export interface PathCardView {
    readonly Match: ResearchRoleMatch;
    readonly Badge: CvBandBadge;
    readonly PercentLabel: string;
    /** 0..100 for the bar's width; produced from `PercentLabel`, the only place an overlap becomes a percent. */
    readonly Percent: number;
    readonly Readiness: readonly CvTextSegment[];
    readonly Covered: string;
    readonly Held: readonly string[];
    readonly More: number;
}

/**
 * The Career Explorer paths view (P6:1640-1655 `vPaths`): one card per matched role. Presentational; the page owns the matches
 * and the profiles and opens a role through the host.
 *
 * Deviations from P6, each on purpose:
 * - P6:445-447 draws a striped `.hi` bar behind the solid `.lo` bar for the score range; the contract has one overlap, so the
 *   bar is the solid fill only, and the "Match" figure is one number, not P6:1648's `lo–hi%`;
 * - P6:1646 draws the role's own icon (`ic(r.icon)`); the wire carries none, so every tile shows the same path glyph;
 * - P6:460's second sentence (the striped part) is dropped, see `PATHS_FOOTNOTE`.
 */
@Component({
    standalone: false,
    selector: 'ra-cx-paths',
    templateUrl: './cx-paths.component.html',
    styleUrl: './cx-paths.component.scss',
})
export class CxPathsComponent {
    @Input() Matches: ResearchCareerMatches | null = null;
    @Input() Profiles: ReadonlyMap<string, ResearchJobProfile> = new Map();

    readonly Footnote = PATHS_FOOTNOTE;

    private memo: { matches: ResearchCareerMatches | null; profiles: ReadonlyMap<string, ResearchJobProfile>; cards: readonly PathCardView[] } | null = null;

    constructor(private readonly host: ExplorerRaHostAdapter) {}

    /** Recomputed only when the matches or the profiles Map change identity (the page replaces the Map on load); the template reads it every pass. */
    get Cards(): readonly PathCardView[] {
        if (this.memo === null || this.memo.matches !== this.Matches || this.memo.profiles !== this.Profiles) {
            this.memo = { matches: this.Matches, profiles: this.Profiles, cards: (this.Matches?.matches ?? []).map((m) => this.Build(m, this.Profiles.get(m.roleRef) ?? null)) };
        }
        return this.memo.cards;
    }

    private Build(match: ResearchRoleMatch, profile: ResearchJobProfile | null): PathCardView {
        const label = PercentLabel(match.overlap);
        const held = match.skills.filter((s) => s.evidenced).map((s) => s.name);
        // P6:1516/1651: "{have} of {n} skills covered". Deviation from the brief's "evidenced + notYetEvidenced": that left out the partial rows
        // (`skills[]` entries that are not evidenced), so the card would disagree with the role view's list. Both read `SkillCoverage`.
        const { have, total } = SkillCoverage(match);
        const covered = `${have} of ${total} skills covered`;
        return {
            Match: match,
            Badge: BandBadge(match.band),
            PercentLabel: label,
            Percent: Number.parseInt(label, 10),
            Readiness: ReadinessLine(match),
            Covered: profile ? `${covered}, ${PostingsOf(profile).toLocaleString('en-US')} postings` : covered,
            Held: held.slice(0, MAX_HELD_PILLS),
            More: Math.max(0, held.length - MAX_HELD_PILLS),
        };
    }

    /** The card and its "Explore role →" open the role (P6:1653 `data-role`). */
    Explore(roleRef: string): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.CareerMap, View: 'role', RecordID: roleRef });
    }
}
